import { Container } from "pixi.js";
import { Spine, type SkeletonData } from "@esotericsoftware/spine-pixi-v8";
import { CoverBackground, sizedVariant } from "@slot/presentation";
import type { PresentationTaskRunner, SpineDriver, ViewportStore } from "@slot/presentation";
import { ModalChrome, bindTap } from "@slot/game-shell";
import type { FeatureTriggerContent, FeatureTriggerPort, LocalizationService } from "@slot/game-shell";
import { PanelMotion, type PanelMotionTuning } from "../PanelMotion";
import { TEXT, value, type FittedLine } from "../textStyles";

/**
 * `FeatureTriggerPanel` from the scene dump (y-up flipped): the 1415×1416 `ResponsiveContainer` (max width 1414) with
 * the title, the amount and the description (wrapped, top-aligned in its box, 100 between lines); the cover is the
 * scene's Spine background (the `spineBackground` prefab: a 4501×2532 box, the skeleton's origin at its bottom centre).
 */
const GEOMETRY = {
  content: { width: 1415, height: 1416.2 },
  limits: { desktop: { maxWidth: 1414 }, mobile: { maxWidth: 1414 }, mobile_landscape: {} },
  title: { y: -590.3, fontSize: 140, box: { width: 1375, height: 177.4 } },
  amount: { y: -398.7, fontSize: 140, box: { width: 1375, height: 196.5 } },
  description: { y: 161.6, fontSize: 80, lineHeight: 100, verticalAlign: "top", box: { width: 1375, height: 774 } },
  cover: { width: 4501, height: 2532, originY: 1266, loop: "animation" },
} as const;

/**
 * The free spins' start behind `FeatureTriggerPort`: over the animated scene background, the feature's title, "You won
 * N Free Spins" and its description, built per feature id (`featuretrigger.<id>.title|description`,
 * `featuretrigger.amount`). The panel only fades (its scale is pinned, `FEATURE_TRIGGER_TUNING.anim`), the cover with it.
 */
export class FeatureTriggerView extends Container implements FeatureTriggerPort {
  private readonly chrome: ModalChrome;
  private readonly motion: PanelMotion;
  private readonly localization: LocalizationService;
  private readonly title: FittedLine;
  private readonly amount: FittedLine;
  private readonly description: FittedLine;
  private readonly cover: CoverBackground | null;
  private readonly coverSpine: Spine | null;

  public constructor(input: {
    backgroundSkeleton: SkeletonData | null;
    spineDriver: SpineDriver;
    viewport: ViewportStore;
    localization: LocalizationService;
    runner: PresentationTaskRunner;
    motion: PanelMotionTuning;
  }) {
    super({ label: "FeatureTriggerPanel" });
    const g = GEOMETRY;
    this.localization = input.localization;
    this.chrome = new ModalChrome({
      label: "root",
      viewport: input.viewport,
      localization: input.localization,
      runner: input.runner,
      content: { ...g.content, limits: g.limits },
    });
    this.addChild(this.chrome);

    // The cover: the scene's animated background, full screen behind the texts.
    if (input.backgroundSkeleton) {
      const box = new Container({ label: "spineBackground" });
      const spine = new Spine({ skeletonData: input.backgroundSkeleton, autoUpdate: false });
      spine.label = "content";
      spine.position.set(0, g.cover.originY);
      input.spineDriver.attach(spine);
      box.addChild(spine);
      this.coverSpine = spine;
      this.cover = new CoverBackground({
        variants: {
          shared: sizedVariant(box, g.cover.width, g.cover.height, {
            onActivate: () => {
              if (spine.skeleton.data.findAnimation(g.cover.loop) && !spine.state.getCurrent(0)) spine.state.setAnimation(0, g.cover.loop, true);
            },
          }),
        },
      });
      this.cover.label = "BackgroundLayer";
      this.cover.bind(input.viewport);
      this.chrome.backdrop.addChild(this.cover);
    } else {
      this.cover = null;
      this.coverSpine = null;
    }

    const panel = new Container({ label: "AnimatedContainer" });
    this.title = value({ style: TEXT.panel, ...g.title, label: "Title" });
    this.amount = value({ style: TEXT.panel, ...g.amount, label: "AmountLabel" });
    this.description = value({ style: TEXT.panel, ...g.description, wrap: true, label: "Description" });
    panel.addChild(this.title, this.amount, this.description);
    this.chrome.content.addChild(panel);
    this.motion = new PanelMotion({
      chrome: this.chrome,
      panel,
      background: this.cover,
      dim: false,
      runner: input.runner,
      tuning: input.motion,
      loopScope: "feature_trigger_idle_pulse",
    });
  }

  public show(): void {
    this.motion.preSeat();
    this.chrome.visible = true;
  }

  public hide(): void {
    this.chrome.visible = false;
    this.motion.stop();
  }

  /** The feature's title and description, and "You won N Free Spins". */
  public setContent(content: FeatureTriggerContent): void {
    const t = this.localization;
    const id = content.featureId;
    this.title.setLine(t.t(`featuretrigger.${id}.title`, t.t("featuretrigger.subtitle", "Free Spins")));
    this.amount.setLine(t.t("featuretrigger.amount", `You won ${content.amount} Free Spins`, { count: content.amount }));
    this.description.setLine(t.t(`featuretrigger.${id}.description`, ""));
  }

  public setSkipEnabled(enabled: boolean): void {
    this.chrome.skipButton.visible = enabled;
  }

  public bindSkipTap(onPress: () => void): () => void {
    return bindTap(this.chrome.skipButton, onPress);
  }

  public playEnterAnimation(): Promise<void> {
    return this.motion.enter();
  }

  public playExitAnimation(): Promise<void> {
    return this.motion.exit();
  }

  public fadeInContinueText(): Promise<void> {
    return this.motion.fadeInContinueText();
  }

  public dispose(): void {
    this.cover?.dispose();
    if (this.coverSpine && !this.coverSpine.destroyed) this.coverSpine.destroy();
    this.chrome.dispose();
    this.destroy({ children: true });
  }
}
